import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { fetchOpenBankingAccounts } from '../query-api'

const POLLING_INTERVAL_MS = 1500
const MAX_POLLING_DURATION_MS = 30000

interface UsePollOpenBankingAutoConnectProps {
  polling?: boolean
  targetAccountIds: string[]
  pollingInterval?: number
  maxPollingDuration?: number
  onSuccess?: () => void
  onTimeout?: () => void
}

export const usePollOpenBankingAutoConnect = ({
  polling = false,
  targetAccountIds,
  pollingInterval = POLLING_INTERVAL_MS,
  maxPollingDuration = MAX_POLLING_DURATION_MS,
  onSuccess,
  onTimeout,
}: UsePollOpenBankingAutoConnectProps) => {
  const { organization } = useUserOrganization()
  const [isActive, setIsActive] = useState(false)
  const [isRefetching, setIsRefetching] = useState(false)
  const doneRef = useRef(false)
  const callbacksRef = useRef({ onSuccess, onTimeout })

  callbacksRef.current = { onSuccess, onTimeout }

  const { data } = useQuery(
    [QueryKeys.OpenBankingAccounts],
    () =>
      fetchOpenBankingAccounts(organization?.id as string, {
        withDeleted: false,
      }),
    {
      enabled: !!organization?.id && isActive,
      refetchInterval: isActive ? pollingInterval : false,
      refetchIntervalInBackground: false,
    },
  )

  const refetchAndProceed = useCallback(async () => {
    doneRef.current = true
    setIsActive(false)
    setIsRefetching(true)
    try {
      await Promise.all([
        queryClient.refetchQueries(QueryKeys.Accounts),
        queryClient.refetchQueries(QueryKeys.BankAccounts),
      ])
    } finally {
      setIsRefetching(false)
      callbacksRef.current.onSuccess?.()
    }
  }, [callbacksRef])

  const areAllAccountsLinked = useMemo(
    () => !!data && targetAccountIds.every((id) => data.find((a) => a.id === id)?.externalAccountId != null),
    [data, targetAccountIds],
  )

  // Start polling and set up timeout
  useEffect(() => {
    if (!polling) {
      return
    }

    doneRef.current = false
    setIsActive(true)

    const timer = window.setTimeout(() => {
      if (doneRef.current) {
        return
      }
      doneRef.current = true
      setIsActive(false)
      callbacksRef.current.onTimeout?.()
    }, maxPollingDuration)

    return () => {
      window.clearTimeout(timer)
      setIsActive(false)
    }
  }, [polling, maxPollingDuration])

  // Detect success
  useEffect(() => {
    if (!isActive || !areAllAccountsLinked || doneRef.current) {
      return
    }

    refetchAndProceed()
  }, [areAllAccountsLinked, isActive, refetchAndProceed])

  return { isPolling: isActive || isRefetching }
}
